import { useEffect, useRef } from 'react';

import { centerOnDate } from '../helpers/geometry';

/**
 * Раскладка графика: ширина, первичная подводка ленты и компенсация прокрутки.
 *
 * Ширину библиотека считает сама и неправильно — вычитает фиксированную величину под
 * полоску ресайза и берёт размеры из устаревшего снимка. Поэтому считаем сами и отдаём
 * через resize-chart, отложив вызов до следующего кадра: без отсрочки собственный
 * пересчёт библиотеки приходит последним и затирает наш.
 *
 * Отдаёт boardRef, который нужно повесить на доску.
 */
const useChartLayout = ({ apiRef, scrollTarget }) => {
  const boardRef = useRef(null);
  const prevChartWidthRef = useRef(0); // прошлая ширина графика — по ней компенсируем прокрутку
  const initialScrollRef = useRef(false); // ленту подводим только один раз, при открытии

  const scrollTargetRef = useRef(scrollTarget);
  scrollTargetRef.current = scrollTarget;

  useEffect(() => {
    const board = boardRef.current;
    if (!board) return undefined;

    const resizeChart = () => {
      const api = apiRef.current;
      if (!api) return;

      const { _scrollSize: scrollSize = 0, _scales: scales, scrollLeft = 0 } = api.getState();

      const grid = board.querySelector('.wx-table-container');
      const resizer = board.querySelector('.wx-resizer');
      const taken = (grid ? grid.offsetWidth : 0) + (resizer ? resizer.offsetWidth : 0);
      const width = board.clientWidth - taken - scrollSize;

      api.exec('resize-chart', {
        width,
        height: board.clientHeight - ((scales && scales.height) || 0),
        scrollSize,
      });

      const target = scrollTargetRef.current;
      if (!initialScrollRef.current && target && width > 0) {
        initialScrollRef.current = true;
        centerOnDate(api, target.date);

        const rowHeight = api.getState().cellHeight;
        if (target.index > 0 && rowHeight) {
          api.exec('scroll-chart', { top: target.index * rowHeight });
        }

        prevChartWidthRef.current = width;
        return;
      }

      // открытая запись забирает место, поэтому двигаем ленту на ту же величину:
      // правый видимый край остаётся там же, где был
      const prevWidth = prevChartWidthRef.current;
      if (prevWidth && prevWidth !== width) {
        api.exec('scroll-chart', { left: Math.max(0, scrollLeft + prevWidth - width) });
      }
      prevChartWidthRef.current = width;
    };

    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(resizeChart);
    });

    observer.observe(board);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [apiRef]);

  return boardRef;
};

export default useChartLayout;
